"use client";

import { useEffect, useRef, useState } from "react";
import dynamic from "next/dynamic";
import { Stat } from "@/types/data";

const CountUp = dynamic(() => import("react-countup"), {
  ssr: false,
  loading: () => <span>0</span>,
});

interface AnimatedStatsProps {
  stats: Stat[];
  className?: string;
}

export function AnimatedStats({ stats, className }: AnimatedStatsProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const [scrollDirection, setScrollDirection] = useState<"up" | "down">("down");
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const [showFinalValue, setShowFinalValue] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (!isClient) return;
    let lastY = window.scrollY;
    const onScroll = () => {
      const currentY = window.scrollY;
      setScrollDirection(currentY > lastY ? "down" : "up");
      lastY = currentY;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isClient]);

  useEffect(() => {
    if (!isClient) return;

    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const nowVisible = entry.isIntersecting;
          setIsVisible(nowVisible);
          if (nowVisible) {
            if (scrollDirection === "down") {
              setShouldAnimate(true);
              setShowFinalValue(false);
            } else {
              setShouldAnimate(false);
              setShowFinalValue(true);
            }
          } else {
            setShouldAnimate(false);
          }
        });
      },
      { threshold: 0.5 }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [isClient, scrollDirection]);

  return (
    <div
      ref={ref}
      className="grid grid-cols-3 gap-3 text-center  stats-section"
    >
      {stats.map((stat, index) => (
        <div
          key={index}
          className="transform transition-all duration-500 hover:scale-105 w-fit mx-auto"
        >
          <h3 className=" text-3xl md:text-4xl font-semibold text-theme-primary leading-[40px] inline-block">
            {isClient && isVisible && shouldAnimate ? (
              <CountUp
                start={0}
                end={stat.value || 0}
                suffix={stat.suffix || ""}
                separator=","
                duration={2}
                delay={index * 0.2}
              />
            ) : isClient && isVisible && showFinalValue ? (
              `${(stat.value || 0).toLocaleString()}${stat.suffix || ""}`
            ) : (
              `0${stat.suffix || ""}`
            )}
          </h3>
          <p
            className={` ${
              className ? className : "text-[#4F5051] font-medium"
            }  text-[13px] md:text-[13.5px] xl:text-[15.5px] leading-[24px] `}
          >
            {stat.description}
          </p>
        </div>
      ))}
    </div>
  );
}
